import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnquiryForm = ({ sourcePage = 'contact', compact = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: 'MBBS',
    message: '',
    source_page: sourcePage,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, course: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim() || formData.name.length < 2) {
      toast.error('Please enter a valid name (minimum 2 characters)');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number (minimum 10 digits)');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, formData);
      setIsSubmitted(true);
      toast.success('Thank you for your enquiry! Our admissions team will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        course: 'MBBS',
        message: '',
        source_page: sourcePage,
      });
      
      // Reset submitted state after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Enquiry submission error:', error);
      toast.error('Failed to submit enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-[#4ECDC4]/10 rounded-xl p-8 text-center" data-testid="enquiry-success">
        <CheckCircle className="w-16 h-16 text-[#4ECDC4] mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-semibold text-[#1A202C] mb-2">
          Thank You!
        </h3>
        <p className="text-gray-600">
          Your enquiry has been submitted successfully. Our admissions team will contact you within 24-48 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="enquiry-form">
      <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-5`}>
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#4ECDC4] focus:ring-[#4ECDC4]"
            required
            data-testid="enquiry-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#4ECDC4] focus:ring-[#4ECDC4]"
            required
            data-testid="enquiry-email-input"
          />
        </div>
      </div>

      <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-5`}>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
            Phone Number <span className="text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="+91 XXXXX XXXXX"
            value={formData.phone}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#4ECDC4] focus:ring-[#4ECDC4]"
            required
            data-testid="enquiry-phone-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="course" className="text-sm font-medium text-gray-700">
            Course Interested In
          </Label>
          <Select value={formData.course} onValueChange={handleSelectChange}>
            <SelectTrigger 
              className="h-12 bg-gray-50 border-gray-200 focus:border-[#4ECDC4] focus:ring-[#4ECDC4]"
              data-testid="enquiry-course-select"
            >
              <SelectValue placeholder="Select a course" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MBBS">MBBS</SelectItem>
              <SelectItem value="General Enquiry">General Enquiry</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!compact && (
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-medium text-gray-700">
            Message (Optional)
          </Label>
          <Textarea
            id="message"
            name="message"
            placeholder="Any specific questions or requirements..."
            value={formData.message}
            onChange={handleChange}
            className="min-h-[120px] bg-gray-50 border-gray-200 focus:border-[#4ECDC4] focus:ring-[#4ECDC4] resize-none"
            data-testid="enquiry-message-textarea"
          />
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-12 bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full font-medium text-base shadow-lg hover:shadow-xl transition-all"
        disabled={isSubmitting}
        data-testid="enquiry-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit Enquiry'
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        By submitting this form, you agree to be contacted by our admissions team.
      </p>
    </form>
  );
};

export default EnquiryForm;
